// Navigation.tsx
import React, {
	createContext,
	useContext,
	useState,
	ReactNode,
	useCallback,
} from 'react';

interface NavigationType {
	showBackButton: boolean;
	onBackButtonClick: null | (() => void);
}

interface NavigationContext {
	navigationConfig: NavigationType;
	updateNavigationConfig: (latestConfig: Partial<NavigationType>) => () => void;
}

const DEFAULT_CONFIG = {
	showBackButton: false,
	onBackButtonClick: null,
};

const Navigation = createContext<NavigationContext | undefined>(undefined);

export const NavigationProvider: React.FC<{ children: ReactNode }> = ({
	children,
}) => {
	const [navigationConfig, setNavigationConfig] =
		useState<NavigationType>(DEFAULT_CONFIG);

	const updateNavigationConfig = useCallback(
		(latestConfig: Partial<NavigationType>) => {
			setNavigationConfig(prevConfig => ({
				...prevConfig,
				...latestConfig,
			}));
			return () => {
				setNavigationConfig(DEFAULT_CONFIG);
			};
		},
		[],
	);

	return (
		<Navigation.Provider value={{ updateNavigationConfig, navigationConfig }}>
			{children}
		</Navigation.Provider>
	);
};

export const useNavigation = (): NavigationContext => {
	const context = useContext(Navigation);
	if (!context) {
		throw new Error('useNavigation must be used within a NavigationProvider');
	}
	return context;
};
